import { ref, computed, onMounted, getCurrentInstance } from 'vue'
import type { Notification, OneSignalNotification } from '../types/notifications'
import type { NotificationFromStrapi } from '@/types/strapiContentTypes'
import { useAuthStore } from '@/stores/authStore'
import { notificationService } from '@/services/notificationService'

const notifications = ref<Notification[]>([])
const isLoading = ref(false)
const hasNewNotification = ref(false)
const NOTIFICATIONS_STORAGE_KEY = 'onesignal_notifications'

// Helper to save notifications to localStorage (backup/cache)
const saveNotifications = () => {
  try {
    localStorage.setItem(NOTIFICATIONS_STORAGE_KEY, JSON.stringify(notifications.value))
  } catch (e) {
    console.error('Failed to save notifications to storage:', e)
  }
}

// Helper to load notifications from localStorage
const loadNotificationsFromStorage = () => {
  try {
    const stored = localStorage.getItem(NOTIFICATIONS_STORAGE_KEY)
    if (stored) {
      notifications.value = JSON.parse(stored).map((n: any) => ({
        ...n,
        timestamp: new Date(n.timestamp) // Rehydrate Date object
      }))
    }
  } catch (e) {
    console.error('Failed to load notifications from storage:', e)
  }
}

const mapStrapiNotification = (n: NotificationFromStrapi): Notification => ({
  id: n.documentId,
  title: n.title,
  message: n.message,
  type: n.type || 'info',
  timestamp: new Date(n.createdAt),
  read: n.read,
  source: n.source || 'local',
  oneSignalId: n.oneSignalId
})

const addNotification = (notification: Omit<Notification, 'id' | 'timestamp'>) => {
  hasNewNotification.value = true
  setTimeout(() => {
    hasNewNotification.value = false
  }, 2000)

  const newNotification: Notification = {
    ...notification,
    id: Date.now().toString(), // Temporary ID
    timestamp: new Date(),
    source: notification.source || 'local',
  }

  notifications.value.unshift(newNotification)

  // Keep only recent 10
  if (notifications.value.length > 10) {
    notifications.value.pop()
  }

  saveNotifications()
}

const handleOneSignalNotification = (osNotification: OneSignalNotification) => {
  // Convert OneSignal notification to our internal format
  const notification: Omit<Notification, 'id' | 'timestamp'> = {
    title: osNotification.title,
    message: osNotification.body,
    type: 'info', // Default type, could be customized based on osNotification.data
    read: false,
    source: 'onesignal',
    oneSignalId: osNotification.id,
  }

  // Add custom handling based on notification data
  if (osNotification.data?.type) {
    const dataType = osNotification.data.type as string
    switch (dataType) {
      case 'success':
      case 'warning':
      case 'error':
        notification.type = dataType as 'success' | 'warning' | 'error'
        break
      default:
        notification.type = 'info'
    }
  }

  addNotification(notification)
  // Removed auto-fetch from backend to preserve local state
}

const fetchNotifications = async () => {
  // Only load from local storage
  loadNotificationsFromStorage()
}

// Export actions for use outside components
export const notificationActions = {
  addNotification,
  handleOneSignalNotification,
  fetchNotifications
}

export function useNotifications() {
  const unreadCount = computed(() => notifications.value.filter((n) => !n.read).length)

  const markAsRead = async (notificationId: string) => {
    const notification = notifications.value.find((n) => n.id === notificationId)
    if (notification && !notification.read) {
      notification.read = true
      saveNotifications()
    }
  }

  const markAllAsRead = async () => {
    const unreadNotifications = notifications.value.filter(n => !n.read)
    if (unreadNotifications.length === 0) return

    // Update UI and save
    notifications.value.forEach((n) => (n.read = true))
    saveNotifications()
  }

  const removeNotification = (notificationId: string) => {
    const index = notifications.value.findIndex((n) => n.id === notificationId)
    if (index > -1) {
      notifications.value.splice(index, 1)
      saveNotifications()
    }
  }

  const handleNotificationClick = (notificationId: string) => {
    markAsRead(notificationId)
    // Logic for navigation if needed based on notification type/data
  }

  const setupOneSignalListeners = () => {
    // OneSignal v16 listeners are now set up in the useOneSignal composable or oneSignalService
  }

  // Initialize notifications on mount if inside a component
  if (getCurrentInstance()) {
    onMounted(() => {
      fetchNotifications()
    })
  }

  return {
    notifications,
    unreadCount,
    isLoading,
    hasNewNotification,
    fetchNotifications,
    handleNotificationClick,
    markAsRead,
    markAllAsRead,
    addNotification,
    removeNotification,
    handleOneSignalNotification,
    setupOneSignalListeners,
  }
}
